import React, { useState, useEffect } from 'react';
import './App.css';
import { 
  SmartSolutions, 
  QuestionsSection, 
  Login
} from './components';
import Register from './Registration';
import AccessSelection from './AccessSelection';
import WorkflowRenderer, { clearWorkflowInstanceCache } from './WorkflowRenderer';
import GuidedPathways from './GuidedPathways';
import FormsLibrary from './FormsLibrary';
import AppealsModule from './AppealsModule';
import Layout from './Layout';
import FamilyLawTopics from './FamilyLawTopics';
import CourtGuidePage from './CourtGuidePage';
import ResourceLibraryPage from './ResourceLibraryPage';
import WritingReview from './WritingReview';
import ReferralAdmin from './ReferralAdmin';
import Directory from './Directory';
import Calendar from './Calendar';
import { PrivacyPolicy, TermsOfService, LegalDisclaimer } from './LegalPages';
import { UserDashboard } from './UserDashboard';
import { authAPI, getToken } from './api';
import { initializeAnalytics, trackPageView, trackUserLogin } from './Analytics';

export const viewForUser = (userData) => (
  ['awaiting_access_selection', 'awaiting_access_fulfillment'].includes(userData?.account_state)
    ? 'access-selection'
    : 'dashboard'
);

function App() {
  const [currentView, setCurrentView] = useState('home');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize analytics
    initializeAnalytics();
    
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data);
          setCurrentView(viewForUser(response.data));
          trackUserLogin();
        } catch (error) {
          console.error('Auth check failed:', error);
          // Token might be expired, will be handled by interceptor
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearWorkflowInstanceCache(user?.id);
      setUser(null);
      setCurrentView('login');
    };

    window.addEventListener('gpj:auth-expired', handleAuthExpired);
    return () => window.removeEventListener('gpj:auth-expired', handleAuthExpired);
  }, [user?.id]);

  useEffect(() => {
    if (!loading) {
      trackPageView(currentView, `Good Parents Justice - ${currentView}`);
    }
  }, [currentView, loading]);

  const handleLogin = (userData) => {
    if (user?.id && user.id !== userData.id) {
      clearWorkflowInstanceCache(user.id);
    }
    setUser(userData);
    setCurrentView(viewForUser(userData));
    trackUserLogin();
  };

  const handleLogout = async () => {
    clearWorkflowInstanceCache(user?.id);
    await authAPI.logout();
    setUser(null);
    setCurrentView('home');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Good Parents Justice...</p>
        </div>
      </div>
    );
  }

  const renderCurrentView = () => {
    switch(currentView) {
      case 'dashboard':
        return user
          ? (['awaiting_access_selection', 'awaiting_access_fulfillment'].includes(user.account_state)
            ? <AccessSelection />
            : <UserDashboard user={user} setCurrentView={setCurrentView} />)
          : renderHomePage();
      case 'access-selection':
        return user ? <AccessSelection /> : <Login onLogin={handleLogin} setCurrentView={setCurrentView} />;
      case 'getting-started':
        return user
          ? (
            <WorkflowRenderer
              key={user.id}
              workflowId="getting-started"
              userId={user.id}
              onNavigate={setCurrentView}
            />
          )
          : <Login onLogin={handleLogin} setCurrentView={setCurrentView} />;
      case 'home':
        return renderHomePage();
      case 'topics':
        return <FamilyLawTopics setCurrentView={setCurrentView} />;
      case 'forms':
        return <FormsLibrary user={user} setCurrentView={setCurrentView} />;
      case 'calendar':
        return <Calendar user={user} setCurrentView={setCurrentView} />;
      case 'resources':
        return <ResourceLibraryPage />;
      case 'directory':
        return <Directory />;
      case 'court-guide':
        return <CourtGuidePage />;
      case 'appeals':
        return <AppealsModule setCurrentView={setCurrentView} />;
      case 'writing-review':
        return user
          ? <WritingReview />
          : <Login onLogin={handleLogin} setCurrentView={setCurrentView} />;
      case 'referral-admin':
        return user?.role === 'admin'
          ? <ReferralAdmin />
          : renderHomePage();
      case 'login':
        return user
          ? <UserDashboard user={user} setCurrentView={setCurrentView} />
          : <Login onLogin={handleLogin} setCurrentView={setCurrentView} />;
      case 'register':
        return user
          ? <UserDashboard user={user} setCurrentView={setCurrentView} />
          : <Register onRegister={handleLogin} setCurrentView={setCurrentView} />;
      case 'privacy':
        return <PrivacyPolicy />;
      case 'terms':
        return <TermsOfService />;
      case 'disclaimer':
        return <LegalDisclaimer />;
      default:
        return renderHomePage();
    }
  };

  const renderHomePage = () => (
    <>
      <GuidedPathways setCurrentView={setCurrentView} />
      <SmartSolutions />
      <QuestionsSection />
    </>
  );

  return (
    <Layout
      currentView={currentView}
      setCurrentView={setCurrentView}
      user={user}
      onLogout={handleLogout}
    >
      {renderCurrentView()}
    </Layout>
  );
}

export default App;
